import { User } from "oidc-client-ts";
import { getConfig } from "../services/configService";
import { LocationType } from "./locationApi.ts";

export interface FilterParameters {
  name: string | null;
  languages: string[];
  instruments: string[];
  genres: string[];
  filters: string[];
  longitude: number | null;
  latitude: number | null;
  radius: number | null;
  locationType: LocationType | null;
}

export interface SearchResult {
  results: SearchResultDto[];
}

export interface SearchResultDto {
  id: string;
  firstName: string;
  lastName: string;
  profilePictureUrl?: string;
  languages: string[];
  instruments: string[];
  genres: string[];
  filters: string[];
  locationId: string;
  distanceInMeters?: number;
}

export async function searchTeachers(
  user: User | undefined | null,
  filterParameters: FilterParameters,
  page: number,
  pageSize: number,
): Promise<SearchResult> {
  const {
    name,
    languages,
    instruments,
    genres,
    filters,
    longitude,
    latitude,
    radius,
    locationType,
  } = filterParameters;

  const config = await getConfig();
  const url = new URL(`${config.api.baseUrl}/public/teachers`);
  url.searchParams.append("page", page.toString());
  url.searchParams.append("pageSize", pageSize.toString());

  if (name !== null) {
    url.searchParams.append("name", name);
  }
  languages.forEach((lang) => {
    url.searchParams.append("languages", lang);
  });
  instruments.forEach((instrument) => {
    url.searchParams.append("instruments", instrument);
  });
  genres.forEach((genre) => {
    url.searchParams.append("genres", genre);
  });
  filters.forEach((filter) => {
    url.searchParams.append("filters", filter);
  });
  if (longitude && latitude && radius) {
    url.searchParams.append("longitude", String(longitude));
    url.searchParams.append("latitude", String(latitude));
    url.searchParams.append("radiusInMeters", String(radius * 1000));
  }
  if (locationType) {
    url.searchParams.append("locationType", locationType);
  }

  let headers;
  if (user && !user.expired && user.access_token) {
    headers = {
      Authorization: `Bearer ${user.access_token}`,
    };
  } else {
    headers = {};
  }

  const response = await fetch(url.toString(), {
    headers: headers,
  });

  if (!response.ok) {
    throw new Error("Failed to fetch user data");
  }

  return response.json();
}

function isLocationType(value: string): value is LocationType {
  return value === "IN_PERSON" || value === "REMOTE";
}

export function parseLocationType(locationType: string): LocationType | null {
  if (isLocationType(locationType)) {
    return locationType;
  }
  return null;
}
